/*
 * Estado de sessao do painel.
 * A verdade e sempre o servidor: /api/auth/me e a fonte, o React so espelha.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Permission, SessionDto } from '@hotspot/shared';
import { ApiRequestError, UNAUTHENTICATED_EVENT, apiRequest } from './api';

interface AuthState {
  session: SessionDto | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<SessionDto>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  can: (permission: Permission) => boolean;
  /** Verdadeiro quando o servidor exige troca de senha antes de liberar o painel. */
  mustChangePassword: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionDto | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setSession(await apiRequest<SessionDto>('/auth/me'));
    } catch (error) {
      // 401 e esperado quando nao ha sessao; qualquer outro erro tambem nao deve
      // travar a tela de login.
      if (!(error instanceof ApiRequestError)) throw error;
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Qualquer 401 vindo de qualquer chamada derruba a sessao local.
  useEffect(() => {
    const onUnauthenticated = () => setSession(null);
    window.addEventListener(UNAUTHENTICATED_EVENT, onUnauthenticated);
    return () => window.removeEventListener(UNAUTHENTICATED_EVENT, onUnauthenticated);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const result = await apiRequest<SessionDto>('/auth/login', {
      method: 'POST',
      body: { email, password },
    });
    setSession(result);
    return result;
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiRequest('/auth/logout', { method: 'POST' });
    } finally {
      setSession(null);
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      session,
      loading,
      login,
      logout,
      refresh,
      mustChangePassword: session?.admin.mustChangePassword ?? false,
      can: (permission) => session?.permissions.includes(permission) ?? false,
    }),
    [session, loading, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth precisa estar dentro de AuthProvider');
  return context;
}
